import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, Form, Input, message, Select, Space } from 'antd';
import { useMediaQuery } from 'react-responsive';
import { editFormSettings, testWebhookEndpoint } from '../../../store/actions/formActions';
import './style.less';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import getWebhookConfig from '../../../utils/webhook-test';
import { isInvalidQuestion } from '../../../utils/antdTableFormat';
import { cloneDeep } from 'lodash-es';

const WebhookModal = ({ form }) => {
  const dispatch = useDispatch();
  const [isLoading, setLoading] = useState(false);
  const [isWebhookTestLoading, setWebhookTestLoading] = useState(false);

  const [disableContentType, setDisableContentType] = useState(false);
  // Used set data structure -> .has is more perfomant than .includes
  // But does cost of initialising a set nullify the gain ?
  const [selectedQuestions, setSelectedQuestions] = useState(new Set());

  const [antdFormInstance] = Form.useForm();

  const webhook = form.integrations?.webhook;

  const questions = useMemo(() => {
    const filteredQuestions = form.formQuestions
      .filter((question) => question.label)
      .filter((question) => !isInvalidQuestion[question.type]);
    const questionsArray = cloneDeep(filteredQuestions);
    questionsArray.push(
      { questionId: 'response_time', label: 'Response time' },
      { questionId: 'response_number', label: 'Response number' },
      { questionId: 'delivered_to', label: 'Delivered to' },
    );
    return questionsArray;
  }, [form.formQuestions]);

  // Filter out non-existent questionIds from the webhook store.
  const webhookKeys = useMemo(() => {
    if (form.integrations?.webhook) {
      const questionsMap = {};
      questions.forEach((question) => (questionsMap[question.questionId] = question));

      const keys = form.integrations?.webhook.keys.filter(
        (item) => questionsMap[item.questionId] && questionsMap[item.questionId].label,
      );
      return keys;
    } else {
      return [];
    }
  }, [form.integrations, questions]);

  const { t } = useTranslation();

  const handleKeysChange = useCallback((keysArray) => {
    const newSelectedQuestions = keysArray
      .filter((item) => item !== undefined)
      .map((key) => key.questionId);
    setSelectedQuestions(new Set(newSelectedQuestions));
  }, []);

  useEffect(() => {
    if (form.integrations?.webhook) {
      handleKeysChange(form.integrations.webhook.keys);
      setDisableContentType(form.integrations.webhook.method === 'get');
    }
  }, [form, handleKeysChange]);

  function handleValuesChange(changedValues, allValues) {
    if (changedValues.keys) {
      handleKeysChange(allValues.keys);
    }
    if (changedValues.method) {
      setDisableContentType(allValues.method === 'get');
    }
  }

  async function onFinish(values) {
    try {
      setLoading(true);
      const event = await dispatch(
        editFormSettings(form.id, {
          whatsappNumber: form.whatsappNumber,
          integrations: {
            ...(form.integrations || {}),
            webhook: values,
          },
        }),
      );
      if (event && event.type === 'EDIT_FORM_FAIL') {
        throw Error();
      } else {
        message.success(t('formSettings.updateSuccess'), 2);
      }
    } catch (error) {
      message.error(t('formSettings.updateFail'), 2);
    } finally {
      antdFormInstance.resetFields();
      setLoading(false);
    }
  }

  async function handleTestWebhook() {
    try {
      setWebhookTestLoading(true);
      await antdFormInstance.validateFields();
      const values = antdFormInstance.getFieldsValue();
      const axiosConfig = getWebhookConfig(values, questions, form.whatsappNumber);
      await testWebhookEndpoint(axiosConfig);
      message.success(t('formSettings.webhook.testSuccess'));
    } catch (error) {
      console.log(error);
      message.error(t('formSettings.webhook.testFail'));
    } finally {
      setWebhookTestLoading(false);
    }
  }

  const isMobile = useMediaQuery({ query: '(max-width: 576px)' });

  return (
    <div className="webhook-integration-card-content">
      <Form
        name="webhook-integration-form"
        layout="vertical"
        onFinish={onFinish}
        form={antdFormInstance}
        initialValues={{
          url: webhook?.url || '',
          method: webhook?.method || 'post',
          contentType: webhook?.contentType || 'application/json',
          headers: webhook?.headers || [],
          keys: webhookKeys || [],
          additionalFields: webhook?.additionalFields || [
            {
              key: 'form_name',
              value: form.name,
            },
          ],
        }}
        onValuesChange={handleValuesChange}
      >
        <Form.Item label="Webhook endpoint URL">
          <Space size={0} className="webhook-url-strip">
            <Form.Item name="method">
              <Select>
                <Select.Option value="get">GET</Select.Option>
                <Select.Option value="post">POST</Select.Option>
              </Select>
            </Form.Item>
            <Form.Item
              name="url"
              rules={[{ required: true, message: t('formSettings.webhook.urlRequired') }]}
            >
              <Input placeholder="https://yourServerAPI.com/webhook" />
            </Form.Item>
          </Space>
        </Form.Item>
        <Space className="webhook-content-type-strip">
          <Form.Item name="contentType" label="Content type">
            <Select disabled={disableContentType}>
              <Select.Option value="application/json">application/json</Select.Option>
              <Select.Option value="application/x-www-form-urlencoded">
                application/x-www-form-urlencoded
              </Select.Option>
            </Select>
          </Form.Item>
        </Space>

        <Form.List name="keys">
          {(fields, { add, remove }) => (
            <Form.Item label={<div>Questions keys</div>} className="webhook-keys-strip">
              <div className="dynamic-input-strips">
                {fields.map((field, i) => (
                  <Space key={i} direction={isMobile ? 'vertical' : 'horizontal'}>
                    <Form.Item
                      {...field}
                      name={[field.name, 'questionId']}
                      rules={[{ required: false, message: 'question is required' }]}
                    >
                      <Select placeholder="question">
                        {questions.map((question) => (
                          <Select.Option
                            key={question.questionId}
                            value={question.questionId}
                            disabled={selectedQuestions.has(question.questionId)}
                          >
                            {['response_time', 'response_number', 'delivered_to'].includes(
                              question.questionId,
                            ) ? (
                              <em>{question.label}</em>
                            ) : (
                              question.label
                            )}
                          </Select.Option>
                        ))}
                      </Select>
                    </Form.Item>
                    <Form.Item
                      name={[field.name, 'key']}
                      rules={[{ required: false, message: 'key is required' }]}
                    >
                      <Input placeholder="key" />
                    </Form.Item>
                    <Form.Item>
                      <Button
                        onClick={() => remove(field.name)}
                        icon={<DeleteOutlined />}
                        type="danger"
                      >
                        {isMobile ? 'Delete' : null}
                      </Button>
                    </Form.Item>
                  </Space>
                ))}
              </div>
              <Button onClick={() => add()} icon={<PlusOutlined />}>
                {t('formSettings.addButton')}
              </Button>
            </Form.Item>
          )}
        </Form.List>
        <Form.List name="additionalFields">
          {(fields, { add, remove }) => (
            <Form.Item
              label={
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <span>Additional fields</span>{' '}
                </div>
              }
            >
              <div className="dynamic-input-strips">
                {fields.map((field, i) => (
                  <Space key={i} direction={isMobile ? 'vertical' : 'horizontal'}>
                    <Form.Item
                      {...field}
                      name={[field.name, 'key']}
                      rules={[{ required: false, message: 'key is required' }]}
                    >
                      <Input placeholder="key" />
                    </Form.Item>
                    <Form.Item name={[field.name, 'value']}>
                      <Input placeholder="value" />
                    </Form.Item>
                    <Form.Item rules={[{ required: false, message: 'value is required' }]}>
                      <Button
                        onClick={() => remove(field.name)}
                        icon={<DeleteOutlined />}
                        type="danger"
                      >
                        {isMobile ? t('delete.ok') : null}
                      </Button>
                    </Form.Item>
                  </Space>
                ))}
              </div>
              <Button onClick={() => add()} icon={<PlusOutlined />}>
                {`${t('formSettings.addButton')} field `}
              </Button>
            </Form.Item>
          )}
        </Form.List>
        <Form.List name="headers">
          {(fields, { add, remove }) => (
            <Form.Item
              label={
                <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                  <span>Headers</span>{' '}
                </div>
              }
            >
              <div className="dynamic-input-strips">
                {fields.map((field, i) => (
                  <Space key={i} direction={isMobile ? 'vertical' : 'horizontal'}>
                    <Form.Item
                      {...field}
                      name={[field.name, 'key']}
                      rules={[{ required: false, message: 'header key is required' }]}
                    >
                      <Input placeholder="header" />
                    </Form.Item>
                    <Form.Item
                      name={[field.name, 'value']}
                      rules={[{ required: false, message: 'header value is required' }]}
                    >
                      <Input placeholder="value" />
                    </Form.Item>
                    <Form.Item>
                      <Button
                        onClick={() => remove(field.name)}
                        icon={<DeleteOutlined />}
                        type="danger"
                      >
                        {isMobile ? 'Delete' : null}
                      </Button>
                    </Form.Item>
                  </Space>
                ))}
              </div>
              <Button onClick={() => add()} icon={<PlusOutlined />}>
                {`${t('formSettings.addButton')} header`}
              </Button>
            </Form.Item>
          )}
        </Form.List>

        <Form.Item className="webhook-action-button-strip">
          <Space>
            <Button
              aria-label="test webhook"
              onClick={handleTestWebhook}
              loading={isWebhookTestLoading}
              disabled={isWebhookTestLoading}
            >
              {t('formSettings.webhook.testButton')}
            </Button>

            <Button htmlType="submit" type="primary" loading={isLoading} disabled={isLoading}>
              {t('common.save')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
};

export default WebhookModal;
